import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Stack,
  Paper,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RepeatIcon from "@mui/icons-material/Repeat";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  DeleteIcon,
  EditPencilIcon,
} from '../../../Icons/DashboardIcons';
// ─── Shared meta pill row ─────────────────────────────────────────────────────
const MetaItem = ({ icon, label }) => (
  <Box display="flex" alignItems="center" gap={0.5}>
    {React.cloneElement(icon, { sx: { fontSize: 13, color: "text.disabled" } })}
    <Typography variant="caption" color="text.secondary" fontWeight={500}>
      {label}
    </Typography>
  </Box>
);

// ─── Status chip ──────────────────────────────────────────────────────────────
// const StatusChip = ({ isActive }) => (
//   <Chip
//     label={isActive ? "Active" : "Inactive"}
//     size="small"
//     color={isActive ? "success" : "default"}
//     variant="outlined"
//     sx={{ fontWeight: 700, fontSize: "0.65rem" }}
//   />
// );

// ─── Action buttons ───────────────────────────────────────────────────────────
const ActionButtons = ({ q, onView, onEdit, onDelete, compact = false }) => {
  if (compact) {
    return (
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="View">
          <IconButton size="small" onClick={() => onView(q)}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => onEdit(q)}>
            <EditPencilIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={() => onDelete(q)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1}>
      <Button size="small" variant="outlined" startIcon={<InfoOutlinedIcon />} onClick={() => onView(q)}>
        View
      </Button>
      <Button size="small" variant="outlined" startIcon={<EditPencilIcon />} onClick={() => onEdit(q)}>
        Edit
      </Button>
      <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(q)}>
        Delete
      </Button>
    </Stack>
  );
};

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = ({ searchQuery }) => (
  <Paper
    variant="outlined"
    sx={{ borderRadius: 3, p: 8, textAlign: "center", borderStyle: "dashed" }}
  >
    
    <Typography fontWeight={600} mb={0.5}>
      {searchQuery ? "No quizzes match your search" : "No quizzes yet"}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {searchQuery ? `Try a different keyword` : "Create your first quiz to get started"}
    </Typography>
  </Paper>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const QuizContent = ({ data, searchQuery, activeView, onView, onEdit, onDelete }) => {
  const filtered = data.filter((q) =>
    q.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (filtered.length === 0) {
    return <EmptyState searchQuery={searchQuery} />;
  }

  /* ── Grid View ── */
/* ── Grid View ── */
  if (activeView === 0) {
    // Helper to get dynamic gradient based on title length / characters so its consistent per quiz
    const getGradient = (title) => {
      const charCode = title?.charCodeAt(0) || 0;
      if (charCode % 3 === 0) return "linear-gradient(135deg, #0f172a 0%, #334155 100%)";
      if (charCode % 3 === 1) return "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)";
      return "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)";
    };

    return (
      <Grid container spacing={3}>
        {filtered.map((q) => (
          <Grid item xs={12} sm={6} md={4} key={q.id}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: "20px",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.03)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                position: "relative",
                overflow: "hidden",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0px 16px 32px rgba(59, 130, 246, 0.12)",
                  borderColor: "rgba(59, 130, 246, 0.2)",
                  "& .quiz-actions": { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              {/* Premium Gradient Top Header */}
              <Box
                sx={{
                  h: "8px",
                  p: 1.5,
                  pb: 3,
                  background: getGradient(q.title),
                  color: "white",
                  position: "relative",
                }}
              >
                {/* Top Action Icons (Visible on Hover) */}
                <Stack 
                  className="quiz-actions"
                  direction="row" 
                  spacing={1} 
                  sx={{ 
                    position: "absolute", 
                    top: 12, 
                    right: 12,
                    opacity: { xs: 1, md: 0 },
                    transform: { xs: "none", md: "translateY(-10px)" },
                    transition: "all 0.3s ease"
                  }}
                >
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onView(q); }}
                      sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", "&:hover": { bgcolor: "white", color: "#1d4ed8" } }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Quiz">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onEdit(q); }}
                      sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", "&:hover": { bgcolor: "white", color: "#1d4ed8" } }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Quiz">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Are you sure you want to delete "${q.title}"?`)) onDelete(q);
                      }}
                      sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", "&:hover": { bgcolor: "#ef4444", color: "white" } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              <CardContent sx={{ flex: 1, pt: 3, pb: "16px !important", display: "flex", flexDirection: "column" }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    fontSize: "1.1rem",
                    color: "#1e293b",
                    lineHeight: 1.3,
                    mb: 2.5,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }}
                >
                  {q.title}
                </Typography>

                <Box sx={{ mt: "auto", display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Box p={1.5} sx={{ bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #f1f5f9" }}>
                    <Stack spacing={1.5}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <MetaItem icon={<QuizOutlinedIcon />} label="Total Questions" />
                        <Typography variant="subtitle2" fontWeight={700} color="#334155">{q.total_questions}</Typography>
                      </Box>
                      <Divider sx={{ borderStyle: "dashed" }} />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <MetaItem icon={<CheckCircleOutlineIcon />} label="Passing Score" />
                        <Chip size="small" label={`${q.passing_score}%`} sx={{ bgcolor: "#dbeafe", color: "#1e40af", fontWeight: 700, fontSize: "0.7rem", height: 20 }} />
                      </Box>
                      <Divider sx={{ borderStyle: "dashed" }} />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <MetaItem icon={<RepeatIcon />} label="Attempts Allowed" />
                        <Typography variant="body2" fontWeight={600} color="#64748b">{q.attempts_allowed}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  /* ── List View ── */
  return (
    <Stack spacing={1.5}>
      {filtered.map((q) => (
        <Card
          key={q.id}
          variant="outlined"
          sx={{
            borderRadius: 3,
            transition: "box-shadow 0.2s, border-color 0.2s",
            "&:hover": { boxShadow: 2, borderColor: "primary.light" },
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">

              {/* Left: title + meta */}
              <Box flex={1} minWidth={0}>
                <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {q.title}
                  </Typography>
                  
                </Box>

                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <MetaItem icon={<QuizOutlinedIcon />} label={`${q.total_questions} Questions`} />
                  <MetaItem icon={<CheckCircleOutlineIcon />} label={`${q.passing_score}% to pass`} />
                  <MetaItem icon={<RepeatIcon />} label={`${q.attempts_allowed} attempt${q.attempts_allowed !== 1 ? "s" : ""}`} />
                </Stack>
              </Box>

              {/* Right: actions */}
              <ActionButtons q={q} onView={onView} onEdit={onEdit} onDelete={onDelete} compact />
            </Box>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

export default QuizContent;